'use client';

import {
	createContext,
	ReactElement,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import DateField from './components/DateField';
import Offline from './components/Offline';
import {
	addYears,
	subDays,
	parse,
	differenceInDays,
	addDays,
	isBefore,
	startOfDay,
	setDefaultOptions,
	Locale,
} from 'date-fns';
import {
	enUS,
	ar,
	de,
	es,
	fr,
	it,
	ja,
	ko,
	pt,
	ru,
	tr,
	zhCN,
	zhTW,
	el,
	ptBR,
} from 'date-fns/locale';
import { toZonedTime } from 'date-fns-tz';
import GuestDropdown from './components/GuestDropdown';
import PromoCodeField from './components/PromoCodeField';
import classnames from 'classnames';
import DestinationPicker from './components/DestinationPicker';
import ErrorBoundary from './components/ErrorBoundary';
import CAWMobileDialog from './components/CAWMobileDialog';
import { translate, triggerEvent, getConfig, getRTL } from '@micro-apps/env';
import Button from '../button';
import { useWindowSize } from '@fshr/hooks';
import { PlanStore, submitSearch, AvailabilityStore } from '@micro-apps/stores';
import { ExperimentsProvider, getPageAttributes } from '@fshr/experiments';
import useSWR from 'swr';
import { defaultFetcher } from '@fshr/shared';

export type CAWProps = {
	properties?: string;
	excludedProperties?: string;
	roomCodes?: string;
	offerCode?: string;
	bestRateGuaranteedURL?: string;
	mode?: string;
	promoCode?: string;
	hidePromoCode?: string;
	variant?: 'default' | 'hero' | string;
	dateFrom?: string;
	dateTo?: string;
	noCalAvail?: string;
	minStay?: string;
	maxRooms?: string;
	custom?: string;
	modelData?: Record<string, unknown>;
	locale: string;
	planStore: PlanStore;
	availabilityStore: AvailabilityStore;
	destinationPlaceholder?: string;
	ppMode?: boolean;
	disableHide?: boolean;
};
export type dateFnsLocaleProps = {
	localeMappings: Record<string, Locale>;
	en?: Record<string, unknown>;
	ar?: Record<string, unknown>;
	allLocales?: Record<string, unknown>;
};
const dateFnsLocale: dateFnsLocaleProps = {
	localeMappings: {
		ar: ar,
		br: ptBR,
		de: de,
		en: enUS,
		es: es,
		fr: fr,
		it: it,
		jp: ja,
		kr: ko,
		pt: pt,
		ru: ru,
		tr: tr,
		zh: zhCN,
		zh_hant: zhTW,
		'es-es': es,
		gr: el,
	},
	en: {
		weekdaysMin: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
	},
	ar: {
		postformat: function (string) {
			return string;
		},
	},
	allLocales: {
		week: { dow: 0, doy: 6 },
	},
};

interface CustomCawComponentEvent extends Event {
	detail: {
		setFocusTo: string;
		selectProperty?: string;
	};
}
const CAW = ({
	properties = '',
	offerCode = '',
	bestRateGuaranteedURL = '',
	mode = '',
	promoCode = '',
	hidePromoCode = '',
	variant = 'default',
	dateFrom = '',
	dateTo = '',
	noCalAvail = '',
	minStay = '',
	maxRooms = '',
	custom = '',
	destinationPlaceholder = '',
	locale = 'en',
	modelData = {},
	planStore,
	availabilityStore,
	roomCodes,
	ppMode = false,
	disableHide = false,
}: CAWProps): ReactElement<any> => {
	const isABF = !!document.querySelector('.AccommodationsBookingFlow');
	const [error, setError] = useState(false);
	const [submitLabel, setSubmitLabel] = useState(
		translate(
			availabilityStore.results || availabilityStore.error ? 'Update Search' : 'Check Rates'
		)
	);
	const [timezone, setTimezone] = useState(modelData?.['timezone'] as string | undefined);
	const [errorPromoCode, setErrorPromoCode] = useState(false);
	setDefaultOptions({ locale: dateFnsLocale.localeMappings[locale] || enUS });
	const [width, height] = useWindowSize();
	const minStayNum = minStay && parseInt(minStay) ? parseInt(minStay) : 0;
	const maxRoomsNum = maxRooms && parseInt(maxRooms) ? parseInt(maxRooms) : 0;
	const url = window.location.pathname;
	const urlQueryString = window.location.search;
	const isPPC = mode.includes('ppc');
	const hasPpcQueryString = urlQueryString.includes('ppc');
	const propertiesArr = properties ? properties.split(',') : [];
	const isSingleProperty = propertiesArr.length === 1;
	const hotelCode = isSingleProperty ? propertiesArr[0] : '';
	const isOffline = planStore.offline();
	const path = document.querySelector("link[rel='canonical']")?.getAttribute('href');
	const pathParts = path?.split('/') || [];
	const isPropertySubpage = hotelCode && pathParts.length > 3;
	const offerDetail = pathParts.indexOf('offers');
	const isPropertyAccomOfferDetailpage =
		isSingleProperty &&
		offerDetail >= 0 &&
		pathParts.length > offerDetail + 1 &&
		!!pathParts[offerDetail + 1];
	const accom = pathParts.indexOf('accommodations');
	const isPropertyAccomDetailpage =
		isSingleProperty && accom >= 0 && pathParts.length > accom + 1 && !!pathParts[accom + 1];
	const isDesktop = width >= 1100;

	const { NEXT_PUBLIC_WEB_BFF_BASE_URL } = getConfig();
	const {
		data,
		error: swrError,
		isLoading,
	} = useSWR<{ enableMultiRoomBookingOnWww: boolean }>(
		`${NEXT_PUBLIC_WEB_BFF_BASE_URL}/caw`,
		defaultFetcher,
		{
			revalidateIfStale: false,
			revalidateOnFocus: false,
			revalidateOnReconnect: false,
		}
	);

	let customObj: Record<string, string> = {};
	try {
		if (custom) {
			customObj = JSON.parse(custom);
		}
		// eslint-disable-next-line no-empty
	} catch (e) {}
	const customVariant = (customObj['variant'] || '') as string;
	const isHeroVariant = variant === 'hero' || customVariant === 'hero';
	const isTallViewport = isHeroVariant && (height || 0) >= 1024;

	const determineInitialDates = useCallback(() => {
		const destination = planStore.destination;
		const tz = destination?.location?.['timezone']
			? destination.location['timezone']
			: (modelData?.['timezone'] as string | undefined);
		if (tz && timezone !== tz) {
			setTimezone(tz);
		}

		const dateFormat = 'yyyy/MM/dd';

		const today = timezone ? toZonedTime(new Date(), timezone) : new Date();

		let flexDatesAllowed = !noCalAvail;
		let firstBookableDate = new Date(today);
		let lastCheckoutDate = subDays(addYears(today, 1), 1);

		let minEnd = addDays(firstBookableDate, 1);
		let minStay = customObj['minStay'] ? parseInt(customObj['minStay'], 10) : 0;
		let maxStay = customObj['maxStay'] ? parseInt(customObj['maxStay'], 10) : 0;
		let maxRooms = customObj['maxRooms'] ? parseInt(customObj['maxRooms'], 10) : 0;

		if (destination || (isSingleProperty && modelData['timezone'])) {
			const flexDates = destination?.bookingConfig?.flexDates;
			flexDatesAllowed = !!flexDates?.enabled && !!flexDates?.daysRange;
			let openingDate = new Date(today);
			if (customObj['dateFrom'] || dateFrom) {
				const openingDateConfig = parse(customObj['dateFrom'] || dateFrom, dateFormat, new Date());
				openingDate = new Date(openingDateConfig);
				if (customObj['dateTo'] || dateTo) {
					const dateToConfig = parse(customObj['dateTo'] || dateTo, dateFormat, new Date());
					maxStay = differenceInDays(dateToConfig, openingDateConfig) + 1;
					flexDatesAllowed = false;
					lastCheckoutDate = dateToConfig;
				}
			} else if (destination?.bookingConfig?.['openingDate'] || modelData['openingDate']) {
				const openingDateConfig = destination?.bookingConfig?.['openingDate']
					? parse(destination.bookingConfig['openingDate'], dateFormat, new Date())
					: parse(modelData['openingDate'] as string, dateFormat, new Date());
				openingDate = new Date(openingDateConfig);
			}

			const advancePurchase = modelData['advancePurchase'] || 0;

			minStay =
				minStayNum ||
				minStay ||
				(modelData['minStay'] as number) ||
				destination?.bookingConfig?.cawDefaults?.minNights ||
				planStore?.globalSettings?.bookingConfig?.cawDefaults?.minNights ||
				1;

			maxStay =
				maxStay ||
				(modelData['maxStay'] as number) ||
				destination?.bookingConfig?.cawDefaults?.maxNights ||
				(planStore?.globalSettings?.bookingConfig?.cawDefaults?.maxNights as number);

			maxRooms =
				maxRoomsNum ||
				maxRooms ||
				(modelData['maxRooms'] as number) ||
				destination?.bookingConfig?.cawDefaults?.maxRooms ||
				(planStore?.globalSettings?.bookingConfig?.cawDefaults?.maxRooms as number);

			const lpt = new Date(today);
			firstBookableDate = addDays(lpt, advancePurchase as number);
			firstBookableDate = isBefore(startOfDay(openingDate), startOfDay(firstBookableDate))
				? firstBookableDate
				: new Date(openingDate);
			minEnd = addDays(firstBookableDate, minStay);
			if (offerCode || isPropertyAccomDetailpage || isPropertyAccomOfferDetailpage) {
				flexDatesAllowed = false;
			}
		} else {
			minStay =
				minStayNum ||
				minStay ||
				(modelData['minStay'] as number) ||
				planStore?.globalSettings?.bookingConfig?.cawDefaults?.minNights ||
				1;

			if (dateFrom) {
				const openingDateConfig = parse(dateFrom, dateFormat, new Date());
				const openingDate = new Date(openingDateConfig);
				if (dateTo) {
					const dateToConfig = parse(dateTo, dateFormat, new Date());
					maxStay = differenceInDays(dateToConfig, openingDateConfig) + 1;
					lastCheckoutDate = dateToConfig;
				}
				const advancePurchase = modelData['advancePurchase'] || 0;
				const lpt = new Date(today);
				firstBookableDate = addDays(lpt, advancePurchase as number);
				firstBookableDate = isBefore(startOfDay(openingDate), startOfDay(firstBookableDate))
					? firstBookableDate
					: new Date(openingDate);
				minEnd = addDays(firstBookableDate, minStay);
			}

			maxStay =
				maxStay ||
				(modelData['maxStay'] as number) ||
				(planStore?.globalSettings?.bookingConfig?.cawDefaults?.maxNights as number);

			maxRooms =
				maxRoomsNum ||
				maxRooms ||
				(modelData['maxRooms'] as number) ||
				(planStore?.globalSettings?.bookingConfig?.cawDefaults?.maxRooms as number);
		}

		maxStay = maxStay || 28;
		maxRooms = maxRooms || 4;

		return {
			flexDatesAllowed,
			firstBookableDate,
			minEnd,
			maxStay,
			maxRoomsComputed: maxRooms,
			minStayComputed: minStay,
			lastCheckoutDate,
		};
	}, [
		customObj['dateFrom'],
		customObj['dateTo'],
		customObj['maxRooms'],
		customObj['maxStay'],
		customObj['minStay'],
		dateFrom,
		dateTo,
		isPropertyAccomDetailpage,
		isPropertyAccomOfferDetailpage,
		isSingleProperty,
		maxRoomsNum,
		minStayNum,
		modelData['advancePurchase'],
		modelData['maxRooms'],
		modelData['maxStay'],
		modelData['minStay'],
		modelData['openingDate'],
		modelData['timezone'],
		noCalAvail,
		offerCode,
		planStore?.destination,
		planStore?.globalSettings?.bookingConfig?.cawDefaults?.maxNights,
		planStore?.globalSettings?.bookingConfig?.cawDefaults?.maxRooms,
		planStore?.globalSettings?.bookingConfig?.cawDefaults?.minNights,
		timezone,
	]);
	useEffect(() => {
		const {
			flexDatesAllowed,
			firstBookableDate,
			minEnd,
			maxStay,
			maxRoomsComputed,
			minStayComputed,
			lastCheckoutDate,
		} = determineInitialDates();
		planStore.updateConfig({
			firstBookableDate,
			lastCheckoutDate,
			flexDatesAllowed,
			minEnd,
			maxStay,
			minStay: minStayComputed,
			maxRooms: maxRoomsComputed,
			enableMultiRoomBookingOnWww: data?.enableMultiRoomBookingOnWww,
		});
	}, [data, determineInitialDates, planStore, planStore.destination, timezone]);

	const loading = planStore.fetching && !isSingleProperty && !timezone;
	const shouldShowLoader = loading && !isHeroVariant;

	if (
		!loading &&
		(!planStore.search.checkinDate ||
			(planStore?.config?.firstBookableDate &&
				isBefore(
					startOfDay(planStore.search.checkinDate),
					startOfDay(planStore?.config?.firstBookableDate)
				)) ||
			(planStore.search.checkoutDate &&
				planStore?.config?.minEnd &&
				isBefore(startOfDay(planStore.search.checkoutDate), startOfDay(planStore?.config?.minEnd))))
	) {
		planStore.updateSearch({
			checkinDate: planStore.search?.hotelCode ? planStore?.config?.firstBookableDate : undefined,
			checkoutDate: planStore.search?.hotelCode ? planStore?.config?.minEnd : undefined,
			promoCode: ppMode ? 'PREFERRED' : promoCode,
			offerCode,
			roomCode: roomCodes,
			showPrivateRetreats: url.includes('/villa-residence-rentals'),
		});
	}

	useEffect(() => {
		const isPrivateRetreat =
			url.endsWith('/villa-residence-rentals') || url.endsWith('/villa-residence-rentals/');
		if (
			isDesktop &&
			(isABF ||
				(!hasPpcQueryString && window.FS?.['propertyInfo']?.isPlp) ||
				(window.FS?.['propertyInfo'] && isPrivateRetreat))
		) {
			window.dispatchEvent(new Event('cawComponentOpen'));
		}
	}, [isDesktop]);

	// [MOD] Hide promo code for hero variant
	const shouldHidePromoCode = hidePromoCode || promoCode || isHeroVariant;

	if (shouldHidePromoCode) {
		planStore.setCustomPromoCode = true;
	}

	// Function to handle the event
	function handleCawComponentOpen(event: any) {
		const customEvent = event as CustomCawComponentEvent;
		if (customEvent?.detail?.selectProperty) {
			planStore?.updateSearch({
				hotelCode: customEvent.detail.selectProperty,
			});
		}
		if (customEvent?.detail?.setFocusTo === 'promoCode') {
			setErrorPromoCode(true);
		}
	}

	useEffect(() => {
		// Adding the event listener to the document
		window.addEventListener('cawComponentOpen', handleCawComponentOpen);
		return () => {
			// Cleaning up the event listener
			window.removeEventListener('cawComponentOpen', handleCawComponentOpen);
		};
	}, []);

	function dispatchCustomEvent() {
		const customEvent: CustomCawComponentEvent = new CustomEvent('cawComponentOpen', {
			detail: {
				setFocusTo: 'promoCode',
			},
		});
		window.dispatchEvent(customEvent);
	}

	useEffect(() => {
		if (availabilityStore.errorCode === 'E0009' || availabilityStore.errorCode === 'E0331') {
			dispatchCustomEvent();
		}
	}, [availabilityStore.errorCode]);

	useEffect(() => {
		if (planStore.search.promoCode === '') {
			setErrorPromoCode(false);
		}
	}, [planStore.search.promoCode]);

	const submitCAWSearch = useCallback(() => {
		setSubmitLabel(translate('Update Search'));
		submitSearch(
			planStore,
			availabilityStore,
			'fourseasons.com',
			'eCAW',
			undefined,
			undefined,
			ppMode
		);
	}, [availabilityStore, planStore]);

	const isUpdateSearchState =
		submitLabel === translate('Update Search') ||
		(planStore.search.completedSearch && !planStore.search.searchIsDirty);
	const submitBaseClasses =
		'form__submit-button h-[40px] w-full rounded-[5px] border text-button2 cursor-pointer transition-all duration-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-button-primary disabled:cursor-not-allowed';
	const submitCheckRatesClasses =
		'border-button-primary bg-button-primary text-button-primary-inverse hover:bg-button-primary-inverse hover:text-button-primary hover:border-button-primary focus-visible:bg-button-primary-pressed focus:text-button-primary-inverse focus:border-button-primary active:bg-button-primary-inverse active:text-button-primary active:border-button-primary disabled:bg-button-primary-disabled disabled:border-button-primary-disabled disabled:text-button-primary-disabled-inverse';
	const submitUpdateSearchClasses =
		'border-button-primary bg-button-primary-inverse text-button-primary hover:bg-button-primary hover:text-button-primary-inverse hover:border-button-primary focus:bg-button-primary-inverse focus:text-button-primary focus:border-button-primary active:bg-button-primary active:text-button-primary-inverse active:border-button-primary disabled:bg-button-primary-disabled-inverse disabled:border-button-primary-disabled disabled:text-button-primary-disabled';

	const wrapperClasses = classNames(
		'@container/layout ReactCAW caw-standalone__wrapper relative w-full p-0',
		{
			loading: shouldShowLoader,
			'caw-ppc__wrapper caw-vertical__wrapper caw-popup__wrapper': isPPC,
			'caw-comp__wrapper': !isPPC,
		}
	);

	const cawComponentClasses = classNames('cawComponent', {
		singleProperty: isSingleProperty,
		'horizontal relative pt-[40px] pb-[20px] bg-surface-raised shadow-raised':
			!isPPC && !isHeroVariant,
		'horizontal relative pt-[40px] pb-[20px] bg-transparent': !isPPC && !isOffline && isHeroVariant,
		'ppc vertical hidden w-full max-w-[35vw] bg-surface-form p-0 min-[961px]:block': isPPC,
		hasDestinationPicker: !isSingleProperty,
		propertySubpage: isPropertySubpage,
		'hasAlert pl-450 spacing-card-padding-y-s': !isPPC && ((isOffline && !isHeroVariant) || error),
		isOffline: isOffline,
		'pt-[40px] pb-[18px]': !isPPC && !isOffline && !error,
		'caw--variant-hero': isHeroVariant && !isOffline,
		'caw--short': !isTallViewport,
		'pt-[40px] pb-[20px] px-[0] bg-transparent': !isPPC && isOffline && isHeroVariant,
	});

	const cawFieldsContainerClasses = classNames('cawFieldsContainer flex w-full', {
		'w-full  max-w-[1440px] flex-col px-10 py-[30px]': isPPC && !isTallViewport,
		'max-w-[1440px] flex-row gap-[12px] items-end pl-[45px] pr-[0px]': !isPPC && !isHeroVariant,
		'pl-0 pr-[45px]': getRTL() && !isPPC && !isHeroVariant,
		'w-full flex-row gap-150 items-end': isHeroVariant && !isTallViewport,
		'w-full max-w-[320px] flex-col spacing-stack-l border border-[1px] border-border-subtle rounded-[1px]':
			isTallViewport,
	});

	const experimentAttributes = useMemo(
		() => getPageAttributes(planStore?.search.hotelCode),
		[planStore?.search.hotelCode]
	);

	return (
		<div className="tailwind-root CAW">
			<ExperimentsProvider attributes={experimentAttributes}>
				<CAWContext.Provider
					value={{
						planStore,
						isSingleProperty,
						isPPC,
						timezone,
						isDesktop,
						isPropertySubpage: !!isPropertySubpage,
						isABF,
						locale,
						dateFnsLocale,
						isHeroVariant,
					}}
				>
					<div className={wrapperClasses} role="dialog" aria-modal="false">
						{isDesktop ? (
							<section className="App-wrapper p-[0px]">
								<div className="App-wrapper__row m-[0px]">
									<div
										className={classNames('m-[0px] max-w-[100vw] w-full', {
											'xl:max-w-[1750px]': isHeroVariant && !isTallViewport,
										})}
									>
										<div className="App-wrapper__anchor m-[0px] w-full">
											<div className="App-wrapper_mainCol relative p-[0px] !w-full">
												<div
													className={cawComponentClasses}
													role="form"
													aria-label="Check Rates and Availability"
													tabIndex={0}
												>
													{shouldShowLoader ? (
														<video
															width="299"
															height="299"
															className="LoadingIndicator-image"
															title={translate('loading indicator')}
															aria-hidden="true"
															autoPlay={true}
															loop={true}
															playsInline={true}
															muted={true}
														>
															<source
																src="/alt/fshr/design3/images/FS-loader-transparent.webm"
																type="video/webm"
															/>
														</video>
													) : !isOffline ? (
														<ErrorBoundary setError={setError}>
															{isPPC ? (
																<div className="cal-focus-trigger ty-c1 relative flex h-[50px] items-center justify-center cursor-pointer bg-black text-center text-[14px] font-bold tracking-[4px] text-white">
																	{translate('Check Rates & Availability')}
																</div>
															) : undefined}
															<div className={cawFieldsContainerClasses}>
																{isHeroVariant ||
																(!isSingleProperty && planStore?.sortedProperties?.length) ? (
																	<DestinationPicker
																		step={0}
																		setStep={() => {
																			//single property
																		}}
																		propertiesArray={propertiesArr}
																		destinationPlaceholder={destinationPlaceholder}
																	/>
																) : undefined}
																<DateField />
																<div
																	className={classnames('Guestdropdown-container', {
																		col: !isPPC,
																		singleProperty: isSingleProperty,
																		'col-lg-12 w-full pt-[42px] pb-[10px] px-0': isPPC,
																		'w-full max-w-[270px] xl:max-w-full xl:min-w-[270px]':
																			isHeroVariant && !isTallViewport,
																	})}
																>
																	<GuestDropdown />
																</div>
																{!shouldHidePromoCode && !ppMode ? (
																	<PromoCodeField
																		variant="inline"
																		errorPromoCode={errorPromoCode}
																	/>
																) : undefined}
																<div
																	className={classNames(
																		'Submit-container col flex flex-col justify-end',
																		{
																			'h-auto w-1/2 min-w-[175px] max-w-none self-center pt-[5px]':
																				isPPC && !isTallViewport,
																			'w-[15%] min-w-[168px] pr-0 max-w-[180px]':
																				!isPPC && !isHeroVariant,
																			'w-full': isHeroVariant,
																			'max-w-[285px]': isHeroVariant && !isTallViewport,
																		}
																	)}
																>
																	<Button
																		intent="unstyled"
																		className={classNames(
																			submitBaseClasses,
																			isUpdateSearchState
																				? submitUpdateSearchClasses
																				: submitCheckRatesClasses,
																			{
																				'bg-button-primary-inverse! text-button-primary! border-button-primary-inverse! hover:bg-button-primary!  hover:text-button-primary-inverse! hover:border-button-primary-inverse! disabled:bg-button-primary-disabled-inverse! disabled:border-button-primary-disabled-inverse! disabled:text-button-primary-disabled!':
																					isHeroVariant,
																			}
																		)}
																		disabled={
																			!planStore.search.hotelCode ||
																			!planStore.search.checkinDate ||
																			!planStore.search.checkoutDate
																		}
																		onClick={() => {
																			if (availabilityStore.error || availabilityStore.results) {
																				triggerEvent('integrated booking engine', 'change search');
																			}
																			submitCAWSearch();
																		}}
																	>
																		{submitLabel}
																	</Button>
																</div>
																{isPPC && (
																	<div className="ppc-brg-cta ty-c1 icon icon-checkmark mt-[10px] flex items-baseline self-center gap-[9px] text-[9px] leading-[1.2] tracking-[0.5px] text-text-primary [&::before]:h-[13px] [&::before]:w-[13px] [&::before]:text-[13px] [&::before]:translate-y-[25%]">
																		<a href={bestRateGuaranteedURL}>
																			<span>{translate('Best Rate Guaranteed')}</span>
																		</a>
																	</div>
																)}
																{!isHeroVariant && !disableHide && !isPPC ? (
																	<button
																		className={classnames(
																			'hideButton text-text-secondary absolute top-0 right-[110px] h-10 p-0 tracking-[2.05px] [&::after]:hidden',
																			{
																				'right-[unset] left-[110px]': getRTL(),
																			}
																		)}
																		aria-controls="NavigationCAW"
																		aria-expanded="true"
																		aria-label="Hide the Check Rates and Availability form"
																		onClick={() =>
																			window.dispatchEvent(new Event('cawComponentClose'))
																		}
																	>
																		<span
																			aria-hidden="true"
																			className="pointer-events-none absolute -top-[10px] left-1/2 z-10 inline-block h-0 w-0 -translate-x-1/2 border-r-[10px] border-l-[10px] border-r-transparent border-l-transparent border-b-[10px] border-b-surface-raised"
																		/>
																		<span className="text-caption">
																			<span className="text-[14px]">{translate('Hide')}</span>
																		</span>
																	</button>
																) : null}
															</div>
														</ErrorBoundary>
													) : (
														<div>
															<Offline
																title={isOffline?.title}
																explanation={isOffline?.explanation}
																phone={isOffline.phone?.plainText}
																email={isOffline?.email}
																buttonText={isOffline.cta?.text}
																buttonLink={isOffline.cta?.link}
																isHeroVariant={isHeroVariant}
															/>
														</div>
													)}
												</div>
											</div>
										</div>
									</div>
								</div>
							</section>
						) : (
							<CAWMobileDialog
								availabilityStore={availabilityStore}
								submitSearch={submitCAWSearch}
								propertiesArray={propertiesArr}
								destinationPlaceholder={destinationPlaceholder}
								ppMode={ppMode}
								hidePromoCode={!!shouldHidePromoCode}
							/>
						)}
					</div>
				</CAWContext.Provider>
			</ExperimentsProvider>
		</div>
	);
};
export default observer(CAW);
export type CAWContextProps = {
	isSingleProperty?: boolean;
	planStore?: PlanStore;
	isPPC?: boolean;
	timezone?: string;
	isDesktop?: boolean;
	isPropertySubpage?: boolean;
	isABF?: boolean;
	isHeroVariant?: boolean;
	destinationPlaceholder?: string;
	locale?: string;
	dateFnsLocale?: dateFnsLocaleProps;
};
export const CAWContext = createContext<CAWContextProps>({});

export const useCAWContext = () => useContext(CAWContext);
